// import React, { useState, useMemo, useCallback } from "react";
// import { useNavigate } from "react-router-dom";
// import { useSelector } from "react-redux";
// import { useGetCoursesQuery, useGetCourseProgressQuery } from "@/features/api/courseApi";
// import { useGetOrdersQuery } from "@/features/api/orderApi";
// import { Skeleton } from "@/components/ui/skeleton";
// import { Button } from "@/components/ui/button";
// import { Progress } from "@/components/ui/progress";
// import { Input } from "@/components/ui/input";
// import { BookOpen, Video, Search } from "lucide-react";
// import { motion, AnimatePresence } from "framer-motion";

// const CourseSkeleton = () => (
//   <div className="w-full animate-pulse space-y-4 p-4">
//     <Skeleton className="h-48 w-full rounded-xl" />
//     <Skeleton className="h-6 w-3/4 rounded" />
//     <Skeleton className="h-4 w-1/2 rounded" />
//     <Skeleton className="h-12 w-full rounded-xl" />
//   </div>
// );

// const CourseCardWithProgress = ({ course, isEnrolled, progress, handleCourseAction }) => {
//   const loading = progress?.isFetching || false;
//   const completedLessons = progress?.completedLessons?.length || 0;
//   const totalLessons = progress?.totalLessons || 0;
//   const percentage = progress?.percentage || 0;

//   let buttonText = isEnrolled
//     ? percentage === 100
//       ? "Review Course"
//       : percentage > 0
//       ? "Continue Learning"
//       : "Start Learning"
//     : course.price && course.price > 0
//     ? `View Course - ₹${course.price}`
//     : "Explore Free Course";

//   return (
//     <motion.div
//       whileHover={{ scale: 1.02 }}
//       transition={{ duration: 0.2 }}
//       className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col overflow-hidden"
//     >
//       <div className="relative">
//         {course.featuredImage ? (
//           <img
//             src={typeof course.featuredImage === "string" ? course.featuredImage : course.featuredImage?.url}
//             alt={course.altText || course.title}
//             className="w-full h-48 object-cover rounded-t-2xl"
//             loading="lazy"
//           />
//         ) : (
//           <div className="w-full h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center rounded-t-2xl">
//             <Video className="h-12 w-12 text-gray-400" />
//           </div>
//         )}
//         {isEnrolled && (
//           <span className="absolute top-3 right-3 bg-green-500 text-white text-xs font-semibold px-2 py-1 rounded-full">
//             Enrolled
//           </span>
//         )}
//       </div>

//       <div className="p-6 flex flex-col flex-grow">
//         <h3 className="font-semibold text-xl line-clamp-2 mb-2 text-gray-800">{course.title}</h3>
//         <p className="text-sm text-gray-600 mb-3">{course.instructorName || "Expert Instructor"}</p>

//         <p className="text-2xl font-bold text-gray-900 mb-4">
//           {course.price ? `₹${course.price}` : "Free"}
//         </p>

//         {isEnrolled && (
//           <div className="mb-4 space-y-2">
//             <div className="flex justify-between text-sm text-gray-600">
//               <span>Progress</span>
//               <span>{percentage}% ({completedLessons}/{totalLessons} lessons)</span>
//             </div>
//             <Progress value={percentage} className="h-2" />
//           </div>
//         )}

//         <Button
//           size="lg"
//           className="w-full rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white transition-all duration-300"
//           onClick={handleCourseAction}
//           disabled={loading}
//           aria-label={buttonText}
//         >
//           <BookOpen className="mr-2 h-5 w-5" />
//           {buttonText}
//         </Button>
//       </div>
//     </motion.div>
//   );
// };

// const Courses = () => {
//   const user = useSelector((state) => state.auth.user);
//   const navigate = useNavigate();
//   const [searchTerm, setSearchTerm] = useState("");

//   // Fetch courses
//   const { data, isLoading, error } = useGetCoursesQuery();
//   const courses = data?.courses || [];

//   // Fetch user's orders
//   const { data: ordersData } = useGetOrdersQuery({ status: "SUCCESS" }, { skip: !user });

//   // Fetch progress for all courses (if logged in)
//   const { data: progressData } = useGetCourseProgressQuery(undefined, { skip: !user });
//   const progressByCourse = useMemo(() => {
//     if (!progressData?.progress) return {};
//     return progressData.progress.reduce((acc, item) => {
//       acc[item.courseId] = item;
//       return acc;
//     }, {});
//   }, [progressData]);

//   // Determine enrolled courses
//   const enrolledCourseIds = useMemo(() => {
//     const idsFromProfile = new Set(user?.enrolledCourses || []);
//     const idsFromOrders = new Set(ordersData?.data?.map((o) => o.courseId) || []);
//     return new Set([...idsFromProfile, ...idsFromOrders]);
//   }, [user, ordersData]);

//   // Filter courses by search
//   const filteredCourses = useMemo(() => {
//     if (!searchTerm.trim()) return courses;
//     return courses.filter(
//       (c) =>
//         c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
//         c.instructorName?.toLowerCase().includes(searchTerm.toLowerCase())
//     );
//   }, [courses, searchTerm]);

//   const handleCourseAction = useCallback(
//     (course) => {
//       navigate(`/courses/${course.slug}`);
//     },
//     [navigate]
//   );

//   return (
//     <div className="bg-gray-100 min-h-screen">
//       <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
//         <motion.div
//           initial={{ opacity: 0, y: -20 }}
//           animate={{ opacity: 1, y: 0 }}
//           transition={{ duration: 0.5 }}
//           className="flex flex-col sm:flex-row justify-between items-center mb-12 gap-4"
//         >
//           <h2 className="text-4xl font-extrabold text-gray-900 tracking-tight">
//             📚 Explore Our Courses
//           </h2>
//           <div className="relative w-full sm:w-80">
//             <Input
//               type="text"
//               placeholder="Search courses or instructors..."
//               value={searchTerm}
//               onChange={(e) => setSearchTerm(e.target.value)}
//               className="pl-12 pr-4 py-3 rounded-xl border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
//               aria-label="Search courses"
//             />
//             <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
//           </div>
//         </motion.div>

//         <AnimatePresence>
//           {isLoading ? (
//             <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
//               {Array.from({ length: 8 }).map((_, i) => (
//                 <CourseSkeleton key={i} />
//               ))}
//             </div>
//           ) : error ? (
//             <motion.div
//               initial={{ opacity: 0 }}
//               animate={{ opacity: 1 }}
//               className="text-center text-red-600 text-xl font-medium p-8 bg-white rounded-xl shadow"
//             >
//               ❌ Failed to load courses. Please try again later.
//             </motion.div>
//           ) : filteredCourses.length === 0 ? (
//             <motion.div
//               initial={{ opacity: 0 }}
//               animate={{ opacity: 1 }}
//               className="text-center text-gray-600 text-xl font-medium p-8 bg-white rounded-xl shadow"
//             >
//               No courses found matching your search.
//             </motion.div>
//           ) : (
//             <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
//               {filteredCourses.map((course) => (
//                 <CourseCardWithProgress
//                   key={course._id}
//                   course={course}
//                   isEnrolled={enrolledCourseIds.has(course._id)}
//                   progress={progressByCourse[course._id] || null}
//                   handleCourseAction={() => handleCourseAction(course)}
//                 />
//               ))}
//             </div>
//           )}
//         </AnimatePresence>
//       </div>
//     </div>
//   );
// };

// export default Courses;

import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import {
  useGetCoursesQuery,
  useGetCourseProgressQuery,
} from "@/features/api/courseApi";

import { useGetOrdersQuery } from "@/features/api/orderApi";

import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";

import { BookOpen, Video, Search } from "lucide-react";
import { motion } from "framer-motion";

/* ------------------------------------------
   SKELETON LOADER
------------------------------------------- */
const CourseSkeleton = () => (
  <div className="w-full animate-pulse space-y-4 p-4">
    <Skeleton className="h-48 w-full rounded-xl" />
    <Skeleton className="h-6 w-3/4 rounded" />
    <Skeleton className="h-4 w-1/2 rounded" />
    <Skeleton className="h-12 w-full rounded-xl" />
  </div>
);

/* ------------------------------------------
   COURSE CARD
------------------------------------------- */
const CourseCardWithProgress = ({ course, isEnrolled, progress, handleOpen }) => {
  const completed = progress?.completedLessons?.length || 0;
  const total = progress?.totalLessons || 0;
  const percent = progress?.percentage || 0;

  const buttonText = isEnrolled
    ? percent >= 100
      ? "Review Course"
      : percent > 0
      ? "Continue Learning"
      : "Start Learning"
    : course.price > 0
    ? `View Course – ₹${course.price}`
    : "Explore Free Course";

  const featuredImg =
    course.featuredImage?.startsWith("/uploads")
      ? `https://fetc.in${course.featuredImage}`
      : course.featuredImage;

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col overflow-hidden"
    >
      <div className="relative">
        {featuredImg ? (
          <img
            src={featuredImg}
            alt={course.title}
            className="w-full h-48 object-cover rounded-t-2xl"
          />
        ) : (
          <div className="w-full h-48 bg-gray-200 flex items-center justify-center rounded-t-2xl">
            <Video className="h-12 w-12 text-gray-400" />
          </div>
        )}

        {isEnrolled && (
          <span className="absolute top-3 right-3 bg-green-600 text-white text-xs px-3 py-1 rounded-full shadow-md">
            Enrolled
          </span>
        )}
      </div>

      <div className="p-6 flex flex-col flex-grow">
        <h3 className="font-semibold text-xl line-clamp-2 mb-2 text-gray-800">
          {course.title}
        </h3>

        <p className="text-sm text-gray-600 mb-3">
          {course.instructorName || "Expert Instructor"}
        </p>

        <p className="text-2xl font-bold text-gray-900 mb-4">
          {course.price > 0 ? `₹${course.price}` : "Free"}
        </p>

        {isEnrolled && (
          <div className="mb-4 space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Progress</span>
              <span>
                {percent}% ({completed}/{total})
              </span>
            </div>
            <Progress value={percent} className="h-2" />
          </div>
        )}

        <Button
          size="lg"
          className="w-full rounded-xl bg-blue-600 text-white hover:bg-blue-700"
          onClick={handleOpen}
        >
          <BookOpen className="mr-2 h-5 w-5" />
          {buttonText}
        </Button>
      </div>
    </motion.div>
  );
};

/* ------------------------------------------
   MAIN PAGE
------------------------------------------- */
const Courses = () => {
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth.user);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);

  /* FETCH ALL COURSES */
  const { data, isLoading, error } = useGetCoursesQuery();
  const courses = data?.courses || [];

  /* FILTER PUBLISHED */
  const publishedCourses = useMemo(
    () => courses.filter((c) => c.status === "published"),
    [courses]
  );

  /* FETCH SUCCESSFUL ORDERS ONLY */
  const { data: ordersData } = useGetOrdersQuery(
    { status: "SUCCESS" },
    { skip: !user }
  );

  const successfulOrders = useMemo(() => {
    return new Set(
      ordersData?.data
        ?.filter((o) => o.status === "SUCCESS")
        ?.map((o) => o.courseId)
    );
  }, [ordersData]);

  /* USER ENROLLED SET (profile-based + successful orders) */
  const enrolledCourseIds = useMemo(() => {
    const set = new Set(user?.enrolledCourses || []);
    successfulOrders.forEach((id) => set.add(id));
    return set;
  }, [user, successfulOrders]);

  /* SEARCH */
  const filteredCourses = useMemo(() => {
    if (!searchTerm) return publishedCourses;

    return publishedCourses.filter((c) =>
      [c.title, c.instructorName]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [searchTerm, publishedCourses]);

  /* SORTING */
  const sortedCourses = useMemo(() => {
    const arr = [...filteredCourses];

    switch (sortBy) {
      case "newest":
        arr.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case "oldest":
        arr.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        break;
      case "priceLow":
        arr.sort((a, b) => (a.price || 0) - (b.price || 0));
        break;
      case "priceHigh":
        arr.sort((a, b) => (b.price || 0) - (a.price || 0));
        break;
    }
    return arr;
  }, [filteredCourses, sortBy]);

  /* PAGINATION */
  const totalPages = Math.ceil(sortedCourses.length / pageSize);
  const paginated = sortedCourses.slice((page - 1) * pageSize, page * pageSize);

  /* OPEN COURSE */
  const handleOpenCourse = (course) => {
    navigate(`/courses/${course.slug}`);
  };

  /* ------------------------------------------
     UI
  ------------------------------------------- */
  return (
    <div className="bg-gray-100 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 py-12">

        {/* HEADER */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-12 gap-6">
          <h2 className="text-4xl font-extrabold">📚 Explore Our Courses</h2>

          {/* SEARCH */}
          <div className="relative w-full sm:w-80">
            <Input
              placeholder="Search courses or instructors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 py-3 rounded-xl"
            />
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          </div>

          {/* SORT */}
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-52">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="oldest">Oldest</SelectItem>
              <SelectItem value="priceLow">Price: Low to High</SelectItem>
              <SelectItem value="priceHigh">Price: High to Low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* GRID */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <CourseSkeleton key={i} />
            ))}
          </div>
        ) : error ? (
          <p className="text-center text-red-500 text-xl">Failed to load courses.</p>
        ) : paginated.length === 0 ? (
          <p className="text-center text-gray-600 text-xl">No matching courses found.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {paginated.map((course) => (
              <CourseCardWithProgress
                key={course._id}
                course={course}
                isEnrolled={enrolledCourseIds.has(course._id)}
                progress={{}} // You can fetch per-course progress if needed
                handleOpen={() => handleOpenCourse(course)}
              />
            ))}
          </div>
        )}

        {/* PAGINATION */}
        <div className="flex justify-between mt-10 items-center">
          {/* PAGE SIZE */}
          <Select
            value={String(pageSize)}
            onValueChange={(v) => {
              setPageSize(Number(v));
              setPage(1);
            }}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Page Size" />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value="8">8 per page</SelectItem>
              <SelectItem value="12">12 per page</SelectItem>
              <SelectItem value="16">16 per page</SelectItem>
            </SelectContent>
          </Select>

          {/* PAGE NAVIGATION */}
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>

            <span className="font-semibold">
              {page} / {totalPages}
            </span>

            <Button
              variant="outline"
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Courses;
